import { once } from 'lodash';
import Router from 'next/router';
import { useEffect, useMemo } from 'react';

import { addErrorEventListener } from '@air/api';

import { Routes } from '~/constants/routes';
import { useLogout } from '~/swr-hooks/auth/useLogout';
import { isMissingAuthError, isNotAuthenticatedError } from '~/utils/auth/isUnauthenticatedError';

type UseHandleUnauthenticatedUserParams = {
  handleError?: () => void;
};

const defaultParams: UseHandleUnauthenticatedUserParams = {
  handleError: () => {
    Router.ready(() => {
      Router.push(Routes.auth.login);
    });
  },
};

/**
 * Prefer `window.location` over `Router.asPath`: missing-auth can fire after
 * `/logout` is already in the address bar while the router still reports the
 * previous page. Snapshotting that URL into `?redirect=` sends login back through logout.
 */
const isOnLogoutPage = (): boolean => {
  if (typeof window !== 'undefined' && window.location.pathname === Routes.auth.logout) {
    return true;
  }

  return Router.asPath.split('?')[0] === Routes.auth.logout;
};

export const useHandleUnauthenticatedUser = ({ handleError }: UseHandleUnauthenticatedUserParams = defaultParams) => {
  const { logout } = useLogout();

  const handleNotAuthenticated = useMemo(
    () =>
      once(() => {
        const currentPath = Router.asPath;

        logout({
          query: currentPath !== Routes.root ? { redirect: currentPath } : undefined,
        });
      }),
    [logout],
  );

  /**
   * This is not inside of a mount useEffect because we want this to run immediately and not after
   * children have mounted so that way we can ensure our listener is setup as quickly as possible.
   */
  const removeListener = addErrorEventListener((error) => {
    if (isOnLogoutPage()) {
      return;
    }

    if (isNotAuthenticatedError(error)) {
      handleNotAuthenticated();
      return;
    }

    if (isMissingAuthError(error)) {
      handleError?.();
    }
  });

  useEffect(() => {
    return () => {
      removeListener();
    };
  }, [removeListener]);
};
